import React from 'react'
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl'

import { CollectionList } from 'shared-global-ec-uikit/components/dist/react/esm/components/collection-list'
import { FlagText } from 'shared-global-ec-uikit/components/dist/react/esm/components/flag-text'
import { LinkCell } from 'shared-global-ec-uikit/components/dist/react/esm/components/link-cell'
import { ContentAlignment } from 'shared-global-ec-uikit/components/dist/react/esm/components/content-alignment'
import { Body } from 'shared-global-ec-uikit/components/dist/react/esm/components/body'
import { Layout } from 'shared-global-ec-uikit/components/dist/react/esm/components/layout'
import LoadingSkeleton from '../LoadingSkeleton'
import TagManager from 'react-gtm-module'
import getConstants from './../../../../constants'
import { BRAND_LIST, DistanceUnit } from 'typings'

import { Accessibility, StoreSize } from 'services/Stores/constants'
import { getStoreNameWithStatus } from 'services/Stores/utils'
import { Title } from 'shared-global-ec-uikit/components/dist/react/esm'
import { InfiniteScroll } from 'components/InfiniteScroll'
import Parser from 'html-react-parser'

type Props = {
  storeList: Array<Components.Schemas.StoreList>
  totalCount?: number
  filterCount: number
  isSearching: boolean
  isSearchingByMapArea?: boolean
  onFilterButtonClick: () => void
  onStoreSelect: (store: Components.Schemas.StoreList) => void
  onLoadMore: () => void
  searchTerm: string | undefined
} & WrappedComponentProps
const constants = getConstants()
const brand = constants.BRAND
const triggerResult = (searchTerm: Props['searchTerm']) => ({
  dataLayer: {
    brand: brand === BRAND_LIST.PL ? brand.toUpperCase() : brand,
    event: 'trackEvent',
    eventCategory: 'storelocation',
    eventAction: 'result',
    eventLabel: searchTerm,
  },
})

const Results = ({ isSearchingByMapArea = false, ...props }: Props) => {
  if (!props.isSearching && typeof props.totalCount === 'undefined') {
    return null
  }

  // TODO: Need to confirm how we're going to show this like, after 2021 Jan.
  // const today = new Date()
  // const weekday: number = today.getDay()
  // const NUMBER_DAY_MAP: string[] = [
  //   'sun',
  //   'mon',
  //   'tue',
  //   'wed',
  //   'thu',
  //   'fri',
  //   'sat',
  // ]
  // const openAtKey = `${NUMBER_DAY_MAP[weekday]}OpenAt` as keyof Store
  // const closeAtKey = `${NUMBER_DAY_MAP[weekday]}CloseAt` as keyof Store

  const hasMore = props.totalCount && props.storeList.length < props.totalCount

  const getStoreTypeText = (text: string) => {
    switch (text) {
      case StoreSize.extraLarge:
        return <FormattedMessage id="text_megastore" />

      case StoreSize.large:
        return <FormattedMessage id="text_largestore" />

      case StoreSize.standard:
        return <FormattedMessage id="text_standard_store" />

      case StoreSize.small:
        return <FormattedMessage id="text_small_store" />

      default:
        return ''
    }
  }

  const renderDistanceUnit = () => {
    return constants.DISTANCE_UNIT == DistanceUnit.MILE ? (
      <FormattedMessage id="text_mile" />
    ) : (
      <FormattedMessage id="text_km" />
    )
  }

  return (
    <>
      <Layout colSpan={{ sm: 4, md: 12, lg: 7, xl: 10 }}>
        <ContentAlignment
          element="div"
          justifyContent="space-between"
          direction="row"
          spread={true}
        ></ContentAlignment>
      </Layout>
      {props.isSearching && (
        <Layout colSpan={{ sm: 4, md: 12, lg: 7, xl: 10 }}>
          <LoadingSkeleton numberOfResults={4} />
        </Layout>
      )}
      <Layout colSpan={{ sm: 4, md: 12, lg: 7, xl: 10 }}>
        {props.storeList.length > 0 && !props.isSearching ? (
          <CollectionList keylines="between-items" spacing={16}>
            {props.storeList.map(store => (
              <LinkCell
                chevron
                key={store.name}
                element="button"
                onClick={() => {
                  TagManager.dataLayer(triggerResult(props.searchTerm))
                  props.onStoreSelect(store)
                }}
                contentWithNoPadding={
                  <>
                    <Title
                      className="shared-global-ec-uikit-mb-spacing-03"
                      color="primary-dark"
                      element="h3"
                      textAlign="left"
                      textTransform="normal"
                      variant="large"
                    >
                      {getStoreNameWithStatus(store)}
                    </Title>
                    {store.announcement && (
                      <Body
                        className="shared-global-ec-uikit-mb-spacing-02"
                        color="primary-dark"
                        element="p"
                        textAlign="left"
                        textTransform="normal"
                        variant="standard"
                      >
                        {Parser(store.announcement)}
                      </Body>
                    )}
                    {store.distance && !isSearchingByMapArea && (
                      <Body
                        className="shared-global-ec-uikit-mb-spacing-02"
                        color="primary-dark"
                        element="p"
                        textAlign="left"
                        textTransform="normal"
                        variant="standard"
                      >
                        {store.distance} {renderDistanceUnit()}
                      </Body>
                    )}
                    {store.productTypeList && (
                      <FlagText
                        className="shared-global-ec-uikit-mb-spacing-01"
                        element="p"
                        variant="large"
                      >
                        {store.productTypeList
                          .map(item => item.name)
                          .join(', ')}
                      </FlagText>
                    )}
                    <FlagText element="p" variant="large">
                      {store.storeType && getStoreTypeText(store.storeType)}{' '}
                      {store.accessibilityFlags?.length
                        ? store.accessibilityFlags.map(item => {
                            if (item.type === Accessibility.parking)
                              return item.name
                          })
                        : null}
                    </FlagText>
                  </>
                }
                variant="noPadding"
              />
            ))}
          </CollectionList>
        ) : (
          !props.isSearching && (
            <Body element="p" className="fr-ec-mb-spacing-04">
              <FormattedMessage id="text_nostore_found" />
            </Body>
          )
        )}

        <InfiniteScroll
          isLoading={props.isSearching}
          hasMore={!!hasMore}
          loadMore={props.onLoadMore}
          i18n={'i18n'}
        />
      </Layout>
    </>
  )
}

export default injectIntl(Results)
